import { CART_ITEM_TYPES, CART_PROPERTY_KEYS } from '@theme/cart-contract';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchCartJson } from '@theme/cart-state';

/**
 * Bundle Builder product-card CTA coupling.
 *
 * The Bundle Builder button block (blocks/_bundle-builder-button.liquid) renders a
 * `[data-bb-product-cta][data-product-id]` wrapper when it sits inside a product card
 * (closest.product present). This module makes that CTA mirror the committed cart and
 * drive the bundle flow:
 *   - Add    → window.bundleBuilderAddProduct(productId): opens the BB drawer + the
 *              secondary drawer (variant picker) for that product.
 *   - Remove → strips that product's bundle line(s) (+ any linked children).
 *
 * A button shows "Remove" when at least one of its product's variants is in the cart
 * with `_item_type: bundle_line_item`. State refreshes on load and on every
 * `cart:update` (fires on add / remove / qty change, and after the cart drawer closes
 * with changes). Standalone bundle buttons (no product, e.g. homepage) are untouched —
 * they keep their `data-bundle-builder-trigger` drawer-open behavior.
 */

const BUNDLE_TYPE = CART_ITEM_TYPES.bundleLineItem;
const ITEM_TYPE_KEY = CART_PROPERTY_KEYS.itemType;
const ADD_TO_CART_ID_KEY = CART_PROPERTY_KEYS.addToCartId;

const DEFAULT_REMOVE_LABEL = 'Remove';
const DEFAULT_ADD_LABEL = 'Add';

const WRAPPER_SELECTOR = '[data-bb-product-cta][data-product-id]';
const CTA_SELECTOR = '[data-bb-product-cta] button, [data-bb-product-cta] a';
const UNAVAILABLE_CLASS = 'bb-cta--unavailable';

/**
 * Product ids the Bundle Builder drawer's availability check reported as not
 * fulfillable for the current store/delivery (via the `ea:bb-product-unavailable`
 * event). Their Add CTA is disabled and clicks are ignored; cleared on store change
 * (`ea:bb-availability-reset` / `ea:bb-store-selected`).
 * @type {Set<string>}
 */
const unavailableProducts = new Set();

/** Reflect the unavailable set onto every bundle CTA (disabled + aria-disabled). */
function applyUnavailable() {
  document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
    const clickable = wrapper.querySelector('button, a');
    if (!clickable) return;
    const bad = unavailableProducts.has(String(wrapper.dataset.productId));
    clickable.classList.toggle(UNAVAILABLE_CLASS, bad);
    if (bad) clickable.setAttribute('aria-disabled', 'true');
    else clickable.removeAttribute('aria-disabled');
  });
}

let syncing = false;
/** The in-flight sync() promise, so overlapping callers await the same reconciliation. */
let syncPromise = null;

/** @returns {Promise<object|null>} */
async function getCart() {
  try {
    // Shared with every other cart reader on the page (@theme/cart-state).
    return await fetchCartJson();
  } catch {
    return null;
  }
}

/**
 * Per-product bundle quantities in the cart.
 * @param {object} cart
 * @returns {Map<string, { qty: number, lines: Array<{ key: string, quantity: number, groupId: string|null }> }>}
 */
function bundleProductQtys(cart) {
  const map = new Map();
  if (!cart || !Array.isArray(cart.items)) return map;
  for (const item of cart.items) {
    const props = item.properties || {};
    if (String(props[ITEM_TYPE_KEY]) === BUNDLE_TYPE) {
      const pid = String(item.product_id);
      const entry = map.get(pid) || { qty: 0, lines: [] };
      entry.qty += item.quantity;
      entry.lines.push({
        key: item.key,
        quantity: item.quantity,
        groupId: props[ADD_TO_CART_ID_KEY] ? String(props[ADD_TO_CART_ID_KEY]) : null,
      });
      map.set(pid, entry);
    }
  }
  return map;
}

/**
 * Apply Add / Remove / Qty visual + data state to a CTA wrapper.
 * @param {Element} wrapper
 * @param {number}  qty  — 0 = add, 1 = remove, >1 = inline qty selector
 */
function applyState(wrapper, qty) {
  const clickable = wrapper.querySelector('button, a');
  if (!clickable) return;
  const addLabel = wrapper.dataset.bbAddLabel || DEFAULT_ADD_LABEL;
  // Localized by the block (bundle_builder.remove); the constant is the English fallback.
  const removeLabel = wrapper.dataset.bbRemoveLabel || DEFAULT_REMOVE_LABEL;
  const qtyValue = wrapper.querySelector('[data-bb-qty-value]');
  const qtyWrap = wrapper.querySelector('[data-bb-qty-wrap]');

  if (qty > 1) {
    wrapper.dataset.bbState = 'qty';
    clickable.setAttribute('aria-hidden', 'true');
    if (qtyValue) qtyValue.textContent = qty;
    if (qtyWrap) qtyWrap.removeAttribute('aria-hidden');
  } else {
    const isRemove = qty === 1;
    wrapper.dataset.bbState = isRemove ? 'remove' : 'add';
    clickable.textContent = isRemove ? removeLabel : addLabel;
    clickable.classList.toggle('bb-cta--remove', isRemove);
    clickable.setAttribute('aria-label', isRemove ? removeLabel : addLabel);
    clickable.removeAttribute('aria-hidden');
    if (qtyWrap) qtyWrap.setAttribute('aria-hidden', 'true');
  }
}

/** Toggle the loading state on a CTA (CSS spinner replaces the label; clicks ignored). */
function setLoading(clickable, on) {
  if (!clickable) return;
  const wrapper = clickable.closest(WRAPPER_SELECTOR);
  clickable.classList.toggle('bb-cta--loading', on);
  if (on) clickable.setAttribute('aria-busy', 'true');
  else clickable.removeAttribute('aria-busy');
  if (wrapper) wrapper.dataset.bbLoading = on ? 'true' : 'false';
}

/**
 * Refresh every bundle CTA's Add/Remove state from the live cart.
 *
 * Overlapping callers (e.g. removeProduct's `await sync()` racing the cart:update
 * listener's sync() triggered by the same dispatch) share the ONE in-flight run and
 * await it until applyState() has finished — so a caller that awaits sync() always
 * sees the reconciled state before continuing (prevents the loader clearing before
 * the label/style flip). The promise is cleared once the run completes.
 * @returns {Promise<void>}
 */
function sync() {
  if (syncing) return syncPromise;
  syncing = true;
  syncPromise = (async () => {
    try {
      const wrappers = document.querySelectorAll(WRAPPER_SELECTOR);
      if (!wrappers.length) return;
      const qtys = bundleProductQtys(await getCart());
      for (const wrapper of wrappers) {
        const entry = qtys.get(String(wrapper.dataset.productId));
        applyState(wrapper, entry ? entry.qty : 0);
      }
    } finally {
      syncing = false;
      syncPromise = null;
    }
  })();
  return syncPromise;
}

/**
 * Remove a product's bundle line(s) — and any linked children — from the
 * cart. `clickable` shows a loading state for the duration of the request.
 */
async function removeProduct(productId, clickable) {
  const done = () => setLoading(clickable, false);

  try {
    const cart = await getCart();
    if (!cart || !Array.isArray(cart.items)) {
      done();
      return;
    }

    const pid = String(productId);
    const groupIds = new Set();
    const updates = {};

    // Bundle lines for this product.
    for (const item of cart.items) {
      const props = item.properties || {};
      if (String(item.product_id) === pid && String(props[ITEM_TYPE_KEY]) === BUNDLE_TYPE) {
        updates[item.key] = 0;
        const gid = props[ADD_TO_CART_ID_KEY];
        if (gid) groupIds.add(String(gid));
      }
    }

    // Any children linked to those bundle lines.
    if (groupIds.size) {
      for (const item of cart.items) {
        const props = item.properties || {};
        const gid = props[ADD_TO_CART_ID_KEY];
        if (gid && groupIds.has(String(gid)) && String(props[ITEM_TYPE_KEY]) !== BUNDLE_TYPE) {
          updates[item.key] = 0;
        }
      }
    }

    if (!Object.keys(updates).length) {
      done();
      return;
    }

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) {
      done();
      return;
    }
    const updated = await res.json();
    // Refresh the header bubble + cart drawer, then flip this CTA's Add/Remove state
    // via sync(), and ONLY THEN clear the loader — so the spinner and the new
    // label/style change in a single paint (no split-second flash of the pre-update
    // button). sync() applies the new state before done() reveals it.
    // Pass BOTH the full cart (resource) and the new count (data.itemCount) so every
    // cart-qty display updates: header-actions.js reads resource.item_count, while
    // cart-icon.js's .cart-bubble reads data.itemCount (undefined would zero it out).
    document.dispatchEvent(new CartUpdateEvent(updated, 'bb-product-card-cta', { itemCount: updated.item_count }));
    await sync();
    done();
  } catch {
    // Leave the committed state untouched; next cart:update reconciles.
    done();
  }
}

/**
 * Adjust a single bundle line item's quantity by `delta` (+1 or −1).
 * Increment targets the first line for the product; decrement targets the last.
 * When a line hits 0, all items sharing its `_add_to_cart_id` group are zeroed
 * (child removal — mirrors `removeProduct`'s group logic).
 *
 * @param {string}  productId
 * @param {number}  delta      — +1 or -1
 * @param {Element} qtyWrap    — the `.bb-cta-qty` element (loading class cleared on done)
 */
async function adjustLineQty(productId, delta, qtyWrap) {
  const done = () => {
    if (!qtyWrap) return;
    qtyWrap.classList.remove('bb-cta-qty--loading');
    qtyWrap.querySelectorAll('.bb-cta-qty__btn').forEach((btn) => {
      btn.disabled = false;
    });
  };
  try {
    const cart = await getCart();
    if (!cart || !Array.isArray(cart.items)) {
      done();
      return;
    }

    const pid = String(productId);
    const lines = [];
    for (const item of cart.items) {
      const props = item.properties || {};
      if (String(item.product_id) === pid && String(props[ITEM_TYPE_KEY]) === BUNDLE_TYPE) {
        lines.push({
          key: item.key,
          quantity: item.quantity,
          groupId: props[ADD_TO_CART_ID_KEY] ? String(props[ADD_TO_CART_ID_KEY]) : null,
        });
      }
    }
    if (!lines.length) {
      done();
      return;
    }

    const targetLine = delta > 0 ? lines[0] : lines[lines.length - 1];
    const newQty = targetLine.quantity + delta;
    const updates = { [targetLine.key]: Math.max(newQty, 0) };

    // Line hits 0 → remove all items sharing the same bundle group (children).
    if (newQty <= 0 && targetLine.groupId) {
      for (const item of cart.items) {
        const props = item.properties || {};
        const gid = props[ADD_TO_CART_ID_KEY];
        if (gid && String(gid) === targetLine.groupId) {
          updates[item.key] = 0;
        }
      }
    }

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) {
      done();
      return;
    }

    const updated = await res.json();
    // Pass BOTH the full cart (resource) and the new count (data.itemCount) so every
    // cart-qty display updates: header-actions.js reads resource.item_count, while
    // cart-icon.js's .cart-bubble reads data.itemCount (undefined would zero it out).
    document.dispatchEvent(new CartUpdateEvent(updated, 'bb-product-card-cta', { itemCount: updated.item_count }));
    await sync();
    done();
  } catch {
    done();
  }
}

/**
 * Start the drawer Add flow for a product. The bundle drawer custom element
 * exposes window.bundleBuilderAddProduct from its connectedCallback, which can be
 * briefly undefined on first paint (module not upgraded yet). Rather than swallow
 * the click, wait a few frames for it so the first click always works (fixes the
 * "first click does nothing, click again" ghost-click).
 * @param {string} productId
 * @param {number} [tries]
 */
function triggerAdd(productId, tries = 60) {
  if (typeof window.bundleBuilderAddProduct === 'function') {
    window.bundleBuilderAddProduct(productId);
    return;
  }
  if (typeof window.openBundleBuilderDrawer === 'function') {
    window.openBundleBuilderDrawer();
    return;
  }
  if (tries <= 0) return;
  requestAnimationFrame(() => triggerAdd(productId, tries - 1));
}

/**
 * Capture-phase click handler — runs before the bundle drawer's bubble-phase
 * trigger handler and any default action, so a product-coupled CTA never also
 * fires the plain drawer-open path.
 * @param {MouseEvent} event
 */
function onClick(event) {
  // SEO-hidden products (product-card[data-seo-hidden="true"]) must not be reachable
  // via the PDP — their purchase is blocked there anyway. On the Bundle Builder page
  // their card image/title still render as PDP links, so intercept those clicks and
  // route them through the same Add flow the CTA uses (opens the BB drawer). Scoped to
  // real navigation links inside the card, excluding the CTA itself.
  const hiddenCard = event.target.closest('.product-card[data-seo-hidden="true"]');
  if (hiddenCard) {
    const link = event.target.closest('a[href]');
    if (link && !link.closest('[data-bb-product-cta]')) {
      // Let modifier-key clicks and non-primary mouse buttons (open in new tab/window,
      // middle-click, etc.) fall through to native link behavior — only ordinary
      // primary clicks are routed through the Add flow.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const productId = hiddenCard.dataset.productId;
      if (productId) handleAddIntent(productId);
      return;
    }
  }

  const clickable = event.target.closest(CTA_SELECTOR);
  if (!clickable) return;
  const wrapper = clickable.closest(WRAPPER_SELECTOR);
  if (!wrapper) return;

  event.preventDefault();
  event.stopPropagation();

  // Qty stepper clicks (minus / plus) — route to adjustLineQty.
  const qtyBtn = event.target.closest('[data-bb-qty-minus], [data-bb-qty-plus]');
  if (qtyBtn) {
    if (wrapper.dataset.bbLoading === 'true') return;
    const qtyWrap = wrapper.querySelector('[data-bb-qty-wrap]');
    if (!qtyWrap || qtyWrap.classList.contains('bb-cta-qty--loading')) return;
    const productId = wrapper.dataset.productId;
    if (!productId) return;
    const delta = qtyBtn.hasAttribute('data-bb-qty-minus') ? -1 : 1;
    qtyWrap.classList.add('bb-cta-qty--loading');
    qtyWrap.querySelectorAll('.bb-cta-qty__btn').forEach((btn) => {
      btn.disabled = true;
    });
    adjustLineQty(productId, delta, qtyWrap);
    return;
  }

  // Ignore clicks while a remove request is in flight.
  if (wrapper.dataset.bbLoading === 'true') return;

  const productId = wrapper.dataset.productId;
  if (!productId) return;

  // Remove is just a cart modification — always allowed, and it never opens the
  // store selector (no store is needed to take an item out of the cart).
  if (wrapper.dataset.bbState === 'remove') {
    setLoading(clickable, true);
    removeProduct(productId, clickable);
    return;
  }

  handleAddIntent(productId);
}

/**
 * Run the Bundle Builder "Add" flow for a product, applying the same guards as a
 * CTA Add click: skip when the product is unavailable, open the store selector when
 * no store is selected, otherwise open the drawer + variant picker. Shared by the
 * CTA button and the SEO-hidden card-link interception below.
 * @param {string} productId
 */
function handleAddIntent(productId) {
  // Product isn't fulfillable for the current store/delivery — CTA is disabled.
  if (unavailableProducts.has(String(productId))) return;

  /* No store yet is no longer a reason to send the shopper somewhere else. Add opens
     the drawer exactly as it does when a store IS selected, and the drawer asks for
     the selection itself — the same way the personalization drawer does on every
     other collection page. Diverting to the header pop-up here meant the shopper lost
     the product they had just chosen and had to find it again. */
  triggerAdd(productId);
}

function init() {
  document.addEventListener('click', onClick, true);
  document.addEventListener(ThemeEvents.cartUpdate, () => sync());

  /* The store no longer gates Add — the drawer asks for it — so nothing here tracks
     whether one is selected. The listener stays for its other job: a store change
     means availability may differ, so every CTA is re-enabled and re-checked on the
     next Add. */
  document.addEventListener('ea:bb-store-selected', () => {
    unavailableProducts.clear();
    applyUnavailable();
  });

  // The BB drawer's availability check flags products that can't be fulfilled for
  // the current store/delivery — disable those Add CTAs here too.
  document.addEventListener('ea:bb-product-unavailable', (event) => {
    const id = event.detail && event.detail.productId;
    if (id == null) return;
    unavailableProducts.add(String(id));
    applyUnavailable();
  });
  document.addEventListener('ea:bb-availability-reset', () => {
    unavailableProducts.clear();
    applyUnavailable();
  });

  sync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
